<main class="tg-form">
	@switch (phase) {
		@case ('loading') {
			<p class="tg-status">{{ t('telegramApp.loading') }}</p>
		}
		@case ('outside') {
			<p class="tg-status">{{ t('telegramApp.outside') }}</p>
		}
		@case ('unsigned') {
			<p class="tg-status">{{ t('telegramApp.unsigned') }}</p>
		}
		@case ('forbidden') {
			<p class="tg-status">{{ t('telegramApp.forbidden') }}</p>
		}
		@case ('unavailable') {
			<p class="tg-status">{{ t('telegramApp.loadError') }}</p>
		}
		@case ('done') {
			<section class="tg-done">
				<h1>{{ t('telegramApp.done.' + result?.status) }}</h1>
				<p class="tg-multiline">{{ result?.message }}</p>
				<button type="button" class="tg-button" (click)="close()">{{ t('telegramApp.close') }}</button>
			</section>
		}
		@case ('form') {
			<header class="tg-header">
				<h1>{{ t('telegramApp.title.' + mode) }}</h1>
				<p>{{ t('telegramApp.hello', { name: userName }) }}</p>
			</header>

			@if (errors.length > 0) {
				<div class="tg-errors" role="alert">
					@for (error of errors; track error) {
						<p>{{ error }}</p>
					}
				</div>
			}

			<!-- Client -->
			<section class="tg-section">
				<h2>{{ t('telegramApp.sections.client') }}</h2>
				@if (canCreateClient) {
					<div class="tg-segmented">
						<button type="button" [class.active]="clientMode === 'existing'" (click)="setClientMode('existing')">
							{{ t('telegramApp.client.existing') }}
						</button>
						<button type="button" [class.active]="clientMode === 'new'" (click)="setClientMode('new')">
							{{ t('telegramApp.client.new') }}
						</button>
					</div>
				}

				@if (clientMode === 'existing') {
					@if (client) {
						<div class="tg-chip">
							<span>
								<strong>{{ client.name }}</strong>
								@if (client.taxId) {
									· {{ client.taxId }}
								}
							</span>
							<button type="button" class="tg-link" (click)="clearClient()">{{ t('telegramApp.client.change') }}</button>
						</div>
					} @else {
						<label class="tg-field">
							<span>{{ t('telegramApp.client.search') }}</span>
							<input
								type="search"
								[ngModel]="search"
								(ngModelChange)="onSearch($event)"
								[placeholder]="t('telegramApp.client.searchPlaceholder')"
								autocomplete="off"
							/>
						</label>
						@if (searching) {
							<p class="tg-hint">{{ t('telegramApp.loading') }}</p>
						}
						@for (result of results; track result.id) {
							<button type="button" class="tg-option" (click)="selectClient(result)">
								<strong>{{ result.name }}</strong>
								@if (result.taxId) {
									<small>{{ result.taxId }}</small>
								}
							</button>
						}
						@if (!searching && search.trim().length >= 2 && results.length === 0) {
							<p class="tg-hint">{{ t('telegramApp.client.noResults') }}</p>
						}
					}
				} @else {
					<div class="tg-segmented">
						<button type="button" [class.active]="newClient.type === 'COMPANY'" (click)="newClient.type = 'COMPANY'">
							{{ t('telegramApp.client.company') }}
						</button>
						<button
							type="button"
							[class.active]="newClient.type === 'INDIVIDUAL'"
							(click)="newClient.type = 'INDIVIDUAL'"
						>
							{{ t('telegramApp.client.individual') }}
						</button>
					</div>
					<label class="tg-field">
						<span>{{ t('telegramApp.fields.name') }} *</span>
						<input type="text" [(ngModel)]="newClient.name" autocomplete="off" />
					</label>
					<label class="tg-field">
						<span>{{ t('telegramApp.fields.taxId') }} *</span>
						<input type="text" [(ngModel)]="newClient.taxId" autocomplete="off" autocapitalize="characters" />
					</label>
					<label class="tg-field">
						<span>{{ t('telegramApp.fields.iban') }} *</span>
						<input type="text" [(ngModel)]="newClient.iban" autocomplete="off" autocapitalize="characters" />
					</label>
					<label class="tg-field">
						<span>{{ t('telegramApp.fields.phone') }}</span>
						<input type="tel" [(ngModel)]="newClient.phone" autocomplete="off" />
					</label>
					<label class="tg-field">
						<span>{{ t('telegramApp.fields.email') }}</span>
						<input type="email" [(ngModel)]="newClient.email" autocomplete="off" />
					</label>
				}
			</section>

			<!-- Supply point -->
			@if (clientMode === 'new' || client) {
				<section class="tg-section">
					<h2>{{ t('telegramApp.sections.cups') }}</h2>
					@if (clientMode === 'existing' && mode === 'NEW') {
						<div class="tg-segmented">
							<button type="button" [class.active]="cupsMode === 'existing'" (click)="cupsMode = 'existing'">
								{{ t('telegramApp.cups.existing') }}
							</button>
							<button type="button" [class.active]="cupsMode === 'new'" (click)="cupsMode = 'new'; cupsId = null">
								{{ t('telegramApp.cups.new') }}
							</button>
						</div>
					}

					@if (cupsMode === 'existing') {
						@for (cups of availableCups; track cups.id) {
							<button type="button" class="tg-option" [class.active]="cupsId === cups.id" (click)="selectCups(cups)">
								<strong>{{ cups.code }}</strong>
								@if (renewalOrigin(cups); as origin) {
									<small>
										{{
											t(origin.status === 'ACTIVE' ? 'telegramApp.cups.activeUntil' : 'telegramApp.cups.expiredOn', {
												marketer: origin.marketerName ?? '—',
												date: originEndDate(origin),
											})
										}}
									</small>
								} @else if (cups.supplyAddress) {
									<small>{{ cups.supplyAddress }}</small>
								}
							</button>
						} @empty {
							<p class="tg-hint">{{ mode === 'RENEWAL' ? t('telegramApp.cups.noneRenewal') : t('telegramApp.cups.none') }}</p>
						}
					} @else {
						<label class="tg-field">
							<span>{{ t('telegramApp.fields.cupsCode') }} *</span>
							<input type="text" [(ngModel)]="newCups.code" autocomplete="off" autocapitalize="characters" />
						</label>
						<label class="tg-field">
							<span>{{ t('telegramApp.fields.supplyAddress') }}</span>
							<input type="text" [(ngModel)]="newCups.supplyAddress" autocomplete="off" />
						</label>
					}
				</section>
			}

			<!-- Contract -->
			<section class="tg-section">
				<h2>{{ t('telegramApp.sections.contract') }}</h2>
				<label class="tg-field">
					<span>{{ t('telegramApp.fields.marketer') }} *</span>
					<select [(ngModel)]="marketerId">
						<option [ngValue]="null">{{ t('telegramApp.select') }}</option>
						@for (marketer of catalog.marketers; track marketer.id) {
							<option [ngValue]="marketer.id">{{ marketer.name }}</option>
						}
					</select>
				</label>
				<label class="tg-field">
					<span>{{ t('telegramApp.fields.product') }}</span>
					<select [(ngModel)]="product">
						<option value="">{{ t('telegramApp.noProduct') }}</option>
						@for (name of catalog.products; track name) {
							<option [value]="name">{{ name }}</option>
						}
					</select>
				</label>
				<label class="tg-field">
					<span>{{ t('telegramApp.fields.tariff') }} *</span>
					<select [(ngModel)]="tariff">
						@for (option of tariffs; track option.value) {
							<option [ngValue]="option.value">{{ option.label }}</option>
						}
					</select>
				</label>
				<div class="tg-row">
					<label class="tg-field">
						<span>{{ t('telegramApp.fields.startDate') }} *</span>
						<input type="date" [ngModel]="startDate" (ngModelChange)="onStartDate($event)" />
					</label>
					<label class="tg-field">
						<span>{{ t('telegramApp.fields.endDate') }} *</span>
						<input type="date" [(ngModel)]="endDate" />
					</label>
				</div>
				@if (powerPeriods.length > 0) {
					<span class="tg-label">{{ t('telegramApp.fields.powers') }}</span>
					<div class="tg-powers">
						@for (period of powerPeriods; track period) {
							<label class="tg-field">
								<span>P{{ period }}</span>
								<input type="text" inputmode="decimal" [(ngModel)]="powers[period]" autocomplete="off" />
							</label>
						}
					</div>
				}
				<label class="tg-field">
					<span>{{ t('telegramApp.fields.consumption') }}{{ collaboratorId ? ' *' : '' }}</span>
					<input type="text" inputmode="decimal" [(ngModel)]="consumption" autocomplete="off" />
				</label>
				<label class="tg-field">
					<span>{{ t('telegramApp.fields.collaborator') }}</span>
					<select [(ngModel)]="collaboratorId">
						<option [ngValue]="null">{{ t('telegramApp.noCollaborator') }}</option>
						@for (collaborator of catalog.collaborators; track collaborator.id) {
							<option [ngValue]="collaborator.id">{{ collaborator.name }}</option>
						}
					</select>
				</label>
				@if (collaboratorId) {
					<p class="tg-hint">{{ t('telegramApp.collaboratorNeedsConsumption') }}</p>
				}
			</section>

			<p class="tg-warning">{{ t('telegramApp.incompleteHint', { parts: incompleteParts.join(' · ') }) }}</p>

			<button type="button" class="tg-button" [disabled]="sending" (click)="submit()">
				{{ sending ? t('telegramApp.sending') : t('telegramApp.submit.' + mode) }}
			</button>
		}
	}
</main>
